import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {AccountService, AlertService, PaymentSourceService, UserService} from '../_services';
import {CrudPagedListComponent} from '../_directives';
import {BankAccountStatus, ChargeStatus, PaymentSource, PaymentSourceType, StripeAccountType, User} from '../_models';
import {Observable} from "rxjs";

@Component({
  moduleId: module.id.toString(),
  templateUrl: './payment-source.component.html'
})
export class PaymentSourceComponent extends CrudPagedListComponent implements OnInit {
  sources$: Observable<PaymentSource[]> = this.data$;
  user: User;
  accountType: StripeAccountType = StripeAccountType.Main;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    private userService: UserService,
    private paymentService: PaymentSourceService,
    private accountService: AccountService,
    protected alertService: AlertService
  ) {
    super(router, location, route, paymentService, alertService);
    this.objectName = 'payment source';
    this.title = 'My Payment Sources';
  }

  ngOnInit() {
    super.ngOnInit();
    const queryParams = this.route.snapshot.queryParams;
    this.accountType = +queryParams['stripe_account_type'] || StripeAccountType.Main;
    this.addFilter('stripe_account_type', this.accountType);

    this.userService.getCurrent()
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        error => {
          this.handleError(error);
        }
      )
  }

  protected onRouteParams(params: {}) {
    this.accountType = +params['stripe_account_type'] || StripeAccountType.Main;
    this.addFilter('stripe_account_type', this.accountType);
  }

  getType(source: PaymentSource) {
    if (source.resourcetype === PaymentSourceType.BankAccount) {
      return 'ACH Debit';
    }

    return 'Credit Card';
  }

  getIcon(source: PaymentSource) {
    if (source.resourcetype === PaymentSourceType.BankAccount) {
      return 'la-bank';
    }

    return 'la-credit-card';
  }

  getStatus(source: PaymentSource) {
    if (source.resourcetype === PaymentSourceType.BankAccount) {
      if (source.account_status === BankAccountStatus.Error) {
        return 'Error';
      }

      if (source.account_status === BankAccountStatus.VerificationFailed) {
        return 'Verification Failed';
      }

      return source.is_verified ? 'Verified' : 'Unverified';
    }

    if (this.isExpired(source)) {
      return 'Expired';
    }

    return (source.charge_status === ChargeStatus.Decline) ? 'Declined' : 'Active';
  }

  getStatusClass(source: PaymentSource) {
    if (source.resourcetype === PaymentSourceType.BankAccount) {
      if ((source.account_status === BankAccountStatus.Error) ||
        (source.account_status === BankAccountStatus.VerificationFailed)) {
        return 'error';
      }

      return source.is_verified ? 'success' : 'warning';
    }

    if (this.isExpired(source)) {
      return 'error';
    }

    return (source.charge_status === ChargeStatus.Decline) ? 'error' : 'success';
  }

  isExpired(source: PaymentSource) {
    if (this.isCard(source)) {
      let today = new Date();

      if (source.exp_year < today.getFullYear()) {
        return true;
      }
      else if ((source.exp_year === today.getFullYear()) && (source.exp_month < (today.getMonth() + 1))) {
        return true;
      }
    }

    return false;
  }

  isCard(source: PaymentSource) {
    return source.resourcetype === PaymentSourceType.Card;
  }

  isBankAccount(source: PaymentSource) {
    return source.resourcetype === PaymentSourceType.BankAccount;
  }

  get canAddCreditCard(): boolean {
    return this.accountType === StripeAccountType.Main;
  }

  get canSetDefault(): boolean {
    return this.accountType === StripeAccountType.Main;
  }

  setDefault(source: PaymentSource) {
    if (!source.is_default && source.is_verified && (this.accountType === StripeAccountType.Main)) {
      this.accountService.patch(this.user.account.id, {default_payment_source: source.id})
        .subscribe(
          data => {
            this.alertService.success('Changed default payment source.');
            this.userService.clearCache();
            this.queryData();
          },
          error => {
            this.handleSubmitError(error);
          });
    }
  }
}
